
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { useAuth } from '@/context/AuthContext';
import { Exhibit } from '@/data';
import { GoogleMap, LoadScript } from '@react-google-maps/api';
import { supabase } from '@/integrations/supabase/client';
import UserLocationMarker from './components/UserLocationMarker';
import ExhibitMarker from './components/ExhibitMarker';
import MapErrorDisplay from './components/MapErrorDisplay';
import CustomInfoWindow from './components/CustomInfoWindow';

interface OutdoorMapProps {
  exhibits: Exhibit[];
}

const OutdoorMap: React.FC<OutdoorMapProps> = ({ exhibits }) => {
  const { language } = useLanguage();
  const { session } = useAuth();
  const navigate = useNavigate();
  const [activeExhibit, setActiveExhibit] = useState<Exhibit | null>(null);
  const [mapError, setMapError] = useState<boolean>(false);
  const [googleMapsApiKey, setGoogleMapsApiKey] = useState<string>('');
  const [isLoadingApiKey, setIsLoadingApiKey] = useState<boolean>(true);
  const mapRef = useRef<google.maps.Map | null>(null);

  // Fetch Google Maps API key securely from edge function
  useEffect(() => {
    const fetchApiKey = async () => {
      try {
        if (!session?.access_token) {
          setMapError(true);
          setIsLoadingApiKey(false);
          return;
        }

        const { data, error } = await supabase.functions.invoke('get-maps-api-key', {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        });

        if (error) {
          console.error('Error fetching Maps API key:', error);
          setMapError(true);
        } else if (data?.apiKey) {
          setGoogleMapsApiKey(data.apiKey);
        } else {
          setMapError(true);
        }
      } catch (error) {
        console.error('Error fetching Maps API key:', error);
        setMapError(true);
      } finally {
        setIsLoadingApiKey(false);
      }
    };

    fetchApiKey();
  }, [session]);

  // Filter exhibits that have GPS coordinates
  const validExhibits = exhibits.filter(e => 
    e.location.latitude && e.location.longitude
  );
  
  // Use the first exhibit as center or set default coordinates
  const defaultCenter = validExhibits.length > 0 
    ? { lat: validExhibits[0].location.latitude!, lng: validExhibits[0].location.longitude! }
    : { lat: 32.0853, lng: 34.7818 }; // Default coordinates (Tel Aviv)

  const mapStyles = {
    width: '100%',
    height: '100%'
  };

  const handleExhibitClick = (exhibitId: number) => {
    navigate(`/exhibit/${exhibitId}`);
  };
  
  // Function to handle map load error
  const handleMapLoadError = () => {
    setMapError(true);
    console.error("Google Maps failed to load correctly");
  };
  
  // Function to handle map load success
  const handleMapLoad = (map: google.maps.Map) => {
    mapRef.current = map;
  };

  // Show loading state while fetching API key
  if (isLoadingApiKey) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
          <p className="text-muted-foreground">Loading map...</p>
        </div>
      </div>
    );
  }

  // If API key is missing, show a warning
  if (!googleMapsApiKey) {
    return <MapErrorDisplay language={language} errorType="missing-key" />;
  }

  // Show error message if map fails to load
  if (mapError) {
    return <MapErrorDisplay language={language} errorType="load-error" />;
  }

  return (
    <div className="w-full h-full">
      <LoadScript 
        googleMapsApiKey={googleMapsApiKey}
        onError={handleMapLoadError}
        language={language === 'en' ? 'en' : 'he'}
      >
        <GoogleMap
          mapContainerStyle={mapStyles}
          center={defaultCenter}
          zoom={15}
          options={{
            zoomControl: true,
            streetViewControl: false, // Disabled Street View control
            mapTypeControl: true,
            fullscreenControl: true,
          }}
          onLoad={handleMapLoad}
        >
          {/* Show markers for exhibits with valid coordinates */}
          {validExhibits.map(exhibit => (
            <ExhibitMarker 
              key={exhibit.id}
              exhibit={exhibit} 
              onClick={() => setActiveExhibit(exhibit)} 
            />
          ))}
          
          {/* Show user's current location if available */}
          {navigator.geolocation && (
            <UserLocationMarker />
          )}
          
          {/* Custom Info Window for active exhibit */}
          {activeExhibit && mapRef.current && (
            <CustomInfoWindow
              exhibit={activeExhibit}
              position={new google.maps.LatLng(
                activeExhibit.location.latitude!, 
                activeExhibit.location.longitude!
              )}
              map={mapRef.current}
              onClose={() => setActiveExhibit(null)}
              onViewExhibit={handleExhibitClick}
              language={language}
            />
          )}
        </GoogleMap>
      </LoadScript>
    </div>
  );
};

export default OutdoorMap;
